/* =====================================================================
   ProMechBC — product-page behaviour.

   Everything here is progressive enhancement: each section renders and
   reads correctly with this file blocked. Kept separate from pmb-site.js
   so the storefront's global bundle stays lean on non-product pages.

   Re-inits on `shopify:section:load` so the theme editor stays live.
   ===================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function each(root, selector, fn) {
    Array.prototype.forEach.call((root || document).querySelectorAll(selector), fn);
  }

  /* Guard so a section re-render never double-binds the same node. */
  function once(el, key) {
    if (el.dataset[key]) return false;
    el.dataset[key] = '1';
    return true;
  }

  /* ---- Spec sheet: search + expand-all ------------------------------ */
  function initSpecSheet(root) {
    each(root, '[data-pmb-spec-sheet]', function (sheet) {
      if (!once(sheet, 'pmbSpec')) return;

      var section = sheet.closest('section') || document;
      var find = section.querySelector('[data-spec-find]');
      var toggle = section.querySelector('[data-spec-toggle-all]');
      var empty = section.querySelector('[data-spec-empty]');
      var groups = Array.prototype.slice.call(sheet.querySelectorAll('[data-spec-group]'));

      /* Remember which groups the merchant chose to open, so clearing the
         search restores that state rather than leaving everything open. */
      var initiallyOpen = groups.map(function (g) { return g.open; });

      if (find) {
        find.addEventListener('input', function () {
          var q = find.value.trim().toLowerCase();
          var anyVisible = false;

          groups.forEach(function (group, i) {
            var rows = group.querySelectorAll('[data-spec-row]');
            var hits = 0;

            Array.prototype.forEach.call(rows, function (row) {
              var match = !q || (row.dataset.specRow || '').indexOf(q) !== -1;
              row.hidden = !match;
              if (match) hits++;
            });

            group.hidden = q ? hits === 0 : false;
            if (!group.hidden) anyVisible = true;
            /* A search should reveal its hits, not make you open them. */
            group.open = q ? hits > 0 : initiallyOpen[i];
          });

          if (empty) empty.hidden = anyVisible;
          if (toggle) toggle.hidden = !!q;
        });
      }

      if (toggle) {
        toggle.addEventListener('click', function () {
          var expand = toggle.getAttribute('aria-pressed') !== 'true';
          groups.forEach(function (g) { g.open = expand; });
          toggle.setAttribute('aria-pressed', String(expand));
          var label = toggle.querySelector('span');
          if (label) label.textContent = expand ? 'Collapse all' : 'Expand all';
        });
      }
    });
  }

  /* ---- Range calculator --------------------------------------------- */
  /* Multipliers are relative to the merchant's stated baseline, so the
     figure shown at the default settings always equals what they typed. */
  function initRangeCalc(root) {
    each(root, '[data-pmb-range-calc]', function (calc) {
      if (!once(calc, 'pmbRange')) return;

      var base = parseFloat(calc.dataset.base) || 0;
      var refWeight = parseFloat(calc.dataset.refWeight) || 75;
      var out = calc.querySelector('[data-range-out]');
      var weight = calc.querySelector('[data-range-weight]');
      var weightOut = calc.querySelector('[data-range-weight-out]');

      function factor(attr) {
        var on = calc.querySelector('[data-range-' + attr + '][aria-pressed="true"]');
        return on ? parseFloat(on.dataset['range' + attr.charAt(0).toUpperCase() + attr.slice(1)]) || 1 : 1;
      }

      function recalc() {
        var kg = weight ? parseFloat(weight.value) : refWeight;

        /* Every extra kilo over the reference rider costs a little range;
           0.45% per kg matches what we see on the bench well enough for an
           estimate, and it is deliberately conservative. */
        var weightFactor = 1 - (kg - refWeight) * 0.0045;
        weightFactor = Math.max(0.55, Math.min(1.25, weightFactor));

        var km = base * weightFactor * factor('terrain') * factor('assist') * factor('temp');
        if (out) out.textContent = Math.round(km);
        if (weightOut) weightOut.textContent = Math.round(kg);
      }

      if (weight) weight.addEventListener('input', recalc);

      each(calc, '[data-range-terrain], [data-range-assist], [data-range-temp]', function (chip) {
        chip.addEventListener('click', function () {
          /* Chips within one group share a data attribute; find the group by
             checking which of the three this chip carries. */
          var group = chip.hasAttribute('data-range-terrain') ? 'terrain'
            : chip.hasAttribute('data-range-assist') ? 'assist' : 'temp';
          each(calc, '[data-range-' + group + ']', function (sibling) {
            sibling.setAttribute('aria-pressed', String(sibling === chip));
          });
          recalc();
        });
      });

      recalc();
    });
  }

  /* ---- Rider height matcher ------------------------------------------ */
  function initFitMatcher(root) {
    each(root, '[data-pmb-fit]', function (fit) {
      if (!once(fit, 'pmbFit')) return;

      var input = fit.querySelector('[data-fit-input]');
      var readout = fit.querySelector('[data-fit-readout]');
      var sizeOut = fit.querySelector('[data-fit-size]');
      var detailOut = fit.querySelector('[data-fit-detail]');
      var result = fit.querySelector('[data-fit-result]');
      var bands = Array.prototype.slice.call(fit.querySelectorAll('[data-fit-size-band]'));
      if (!input || !bands.length) return;

      function match() {
        var cm = parseFloat(input.value);
        if (readout) readout.textContent = cm + ' cm';

        var hit = null;
        bands.forEach(function (band) {
          var min = parseFloat(band.dataset.min);
          var max = parseFloat(band.dataset.max);
          var on = cm >= min && cm <= max;
          band.classList.toggle('is-match', on);
          if (on && !hit) hit = band;
        });

        /* Outside every published band is a real answer, not an error —
           say so plainly rather than guessing a size. */
        if (hit) {
          if (sizeOut) sizeOut.textContent = hit.dataset.name;
          if (detailOut) detailOut.textContent = hit.dataset.detail || '';
          if (result) result.classList.remove('is-none');
        } else {
          if (sizeOut) sizeOut.textContent = 'Talk to us';
          if (detailOut) {
            detailOut.textContent = 'You sit outside the published range for this frame — we will find you something that fits properly.';
          }
          if (result) result.classList.add('is-none');
        }
      }

      input.addEventListener('input', match);
      match();
    });
  }

  /* ---- Fitment model search ------------------------------------------ */
  function initFitment(root) {
    each(root, '[data-pmb-fitment]', function (wrap) {
      if (!once(wrap, 'pmbFitment')) return;

      var input = wrap.querySelector('[data-fitment-search]');
      var count = wrap.querySelector('[data-fitment-count]');
      var empty = wrap.querySelector('[data-fitment-empty]');
      var models = Array.prototype.slice.call(wrap.querySelectorAll('[data-fitment-model]'));
      if (!input) return;

      input.addEventListener('input', function () {
        var q = input.value.trim().toLowerCase();
        var shown = 0;

        models.forEach(function (model) {
          var on = !q || (model.dataset.fitmentModel || '').indexOf(q) !== -1;
          model.hidden = !on;
          if (on) shown++;
        });

        if (count) count.textContent = shown;
        if (empty) empty.hidden = shown > 0;
      });
    });
  }

  /* ---- Bundle builder ------------------------------------------------- */
  function initBundle(root) {
    each(root, '[data-pmb-bundle]', function (form) {
      if (!once(form, 'pmbBundle')) return;

      var savingPct = parseFloat(form.dataset.savingPct) || 0;
      var anchor = form.querySelector('[data-bundle-price]:not([data-bundle-check])');
      var anchorCents = anchor ? parseInt(anchor.dataset.bundlePrice, 10) || 0 : 0;
      var checks = Array.prototype.slice.call(form.querySelectorAll('[data-bundle-check]'));
      var subtotalEl = form.querySelector('[data-bundle-subtotal]');
      var discountEl = form.querySelector('[data-bundle-discount]');
      var totalEl = form.querySelector('[data-bundle-total]');
      var submit = form.querySelector('[data-bundle-submit]');

      /* Dawn does not ship Shopify.formatMoney, so the shop's own money format
         is printed onto the form and interpolated here. Without it the totals
         would render as a bare "1695.2" and read as a bug. */
      var moneyFormat = form.dataset.moneyFormat || '${{amount}}';

      function group(numberString) {
        return numberString.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      }

      function money(cents) {
        var whole = Math.floor(Math.abs(cents) / 100);
        var frac = ('0' + (Math.abs(cents) % 100)).slice(-2);

        return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, token) {
          switch (token) {
            case 'amount_no_decimals':
              return group(String(whole));
            case 'amount_with_comma_separator':
              return group(String(whole)).replace(/,/g, '.') + ',' + frac;
            case 'amount_no_decimals_with_comma_separator':
              return group(String(whole)).replace(/,/g, '.');
            case 'amount_with_apostrophe_separator':
              return group(String(whole)).replace(/,/g, "'") + '.' + frac;
            default: /* amount */
              return group(String(whole)) + '.' + frac;
          }
        });
      }

      function recalc() {
        var accessories = 0;
        checks.forEach(function (c) {
          if (c.checked && !c.disabled) accessories += parseInt(c.dataset.bundlePrice, 10) || 0;
        });

        /* The discount applies to the accessories only, never to the bike —
           which is how the matching automatic discount is set up in admin. */
        var discount = Math.round(accessories * (savingPct / 100));
        var subtotal = anchorCents + accessories;

        if (subtotalEl) subtotalEl.textContent = money(subtotal);
        if (discountEl) discountEl.textContent = '−' + money(discount);
        if (totalEl) totalEl.textContent = money(subtotal - discount);
      }

      checks.forEach(function (c) { c.addEventListener('change', recalc); });
      recalc();

      form.addEventListener('submit', function (e) {
        if (!window.fetch) return; /* No fetch: let the plain form add the bike. */
        e.preventDefault();

        var items = [];
        var anchorId = form.querySelector('input[name="id"]');
        if (anchorId && anchorId.value) items.push({ id: Number(anchorId.value), quantity: 1 });
        checks.forEach(function (c) {
          if (c.checked && !c.disabled) items.push({ id: Number(c.value), quantity: 1 });
        });
        if (!items.length) return;

        if (submit) submit.setAttribute('aria-busy', 'true');

        fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        })
          .then(function (r) { return r.json(); })
          .then(function () {
            /* Reuse the site's own cart feedback when it is present. */
            if (typeof window.pmbToast === 'function') window.pmbToast('Bundle added to your cart');
            document.dispatchEvent(new CustomEvent('pmb:cart-updated'));
            var drawer = document.querySelector('cart-drawer');
            if (drawer && typeof drawer.open === 'function') {
              fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + '?sections=cart-drawer')
                .finally(function () { window.location.reload(); });
            } else {
              window.location.href = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart';
            }
          })
          .catch(function () {
            window.location.href = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart';
          })
          .finally(function () {
            if (submit) submit.removeAttribute('aria-busy');
          });
      });
    });
  }

  /* ---- Review photo overlay (swipe across every photo) ---------------- */
  /* Most review widgets trap you in one reviewer's gallery. This collects
     every photo in the section so prev/next crosses reviewer boundaries. */
  function initReviewGallery(root) {
    each(root, '[data-pmb-review-photos]', function (scope) {
      if (!once(scope, 'pmbShots')) return;

      var shots = Array.prototype.slice.call(scope.querySelectorAll('[data-review-shot]'));
      if (!shots.length) return;

      var overlay = document.createElement('div');
      overlay.className = 'pmb-shots';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-label', 'Customer photos');
      overlay.hidden = true;
      overlay.innerHTML =
        '<button class="pmb-shots__close" type="button" aria-label="Close">&times;</button>' +
        '<button class="pmb-shots__nav pmb-shots__nav--prev" type="button" aria-label="Previous photo">&#8249;</button>' +
        '<figure class="pmb-shots__stage"><img alt=""><figcaption></figcaption></figure>' +
        '<button class="pmb-shots__nav pmb-shots__nav--next" type="button" aria-label="Next photo">&#8250;</button>';
      document.body.appendChild(overlay);

      var img = overlay.querySelector('img');
      var cap = overlay.querySelector('figcaption');
      var index = 0;
      var opener = null;

      function show(i) {
        index = (i + shots.length) % shots.length;
        var shot = shots[index];
        img.src = shot.dataset.full || shot.currentSrc || shot.src;
        img.alt = shot.alt || '';
        cap.textContent = (shot.dataset.author ? shot.dataset.author + ' · ' : '') +
          (index + 1) + ' of ' + shots.length;
      }

      function open(i, from) {
        opener = from || null;
        show(i);
        overlay.hidden = false;
        document.documentElement.classList.add('pmb-shots-open');
        overlay.querySelector('.pmb-shots__close').focus();
      }

      function close() {
        overlay.hidden = true;
        document.documentElement.classList.remove('pmb-shots-open');
        if (opener) opener.focus();
      }

      shots.forEach(function (shot, i) {
        shot.style.cursor = 'zoom-in';
        shot.addEventListener('click', function () { open(i, shot); });
      });

      overlay.querySelector('.pmb-shots__close').addEventListener('click', close);
      overlay.querySelector('.pmb-shots__nav--prev').addEventListener('click', function () { show(index - 1); });
      overlay.querySelector('.pmb-shots__nav--next').addEventListener('click', function () { show(index + 1); });
      overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });

      document.addEventListener('keydown', function (e) {
        if (overlay.hidden) return;
        if (e.key === 'Escape') close();
        if (e.key === 'ArrowLeft') show(index - 1);
        if (e.key === 'ArrowRight') show(index + 1);
      });

      /* Touch: a horizontal drag over 40px moves one photo. */
      var startX = null;
      overlay.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
      overlay.addEventListener('touchend', function (e) {
        if (startX === null) return;
        var dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) show(index + (dx < 0 ? 1 : -1));
        startX = null;
      });
    });
  }

  /* ---- Gallery thumb rail -------------------------------------------- */
  /* On desktop the rail is absolutely positioned (pmb-sections-2.css) so a
     long thumb list can never make the sticky gallery taller than the photo;
     it scrolls inside the stage's height instead. Two things follow from
     that: the edge fades need to know where the scroll sits, and the active
     thumb has to be pulled into view when the media changes. */
  function initGalleryRail(root) {
    each(root, '.pmb-product media-gallery .thumbnail-slider', function (rail) {
      if (!once(rail, 'pmbRail')) return;

      var list = rail.querySelector('.thumbnail-list');
      if (!list) return;

      var desktop = window.matchMedia('(min-width: 990px)');

      function sync() {
        var max = rail.scrollHeight - rail.clientHeight;
        /* Below 990px the rail is a plain horizontal slider again — leave it. */
        var scrollable = desktop.matches && max > 2;
        rail.classList.toggle('is-scrollable', scrollable);
        rail.classList.toggle('is-at-top', !scrollable || rail.scrollTop <= 2);
        rail.classList.toggle('is-at-bottom', !scrollable || rail.scrollTop >= max - 2);
      }

      function reveal() {
        if (!desktop.matches) return;

        var current = list.querySelector('button[aria-current="true"]');
        var item = current && current.closest('.thumbnail-list__item');
        if (!item) return;

        var box = rail.getBoundingClientRect();
        var thumb = item.getBoundingClientRect();

        /* Keep a 10px margin so the revealed thumb never sits flush against
           the fade and read as half-hidden. */
        var delta = 0;
        if (thumb.top < box.top + 10) delta = thumb.top - box.top - 10;
        else if (thumb.bottom > box.bottom - 10) delta = thumb.bottom - box.bottom + 10;
        if (!delta) return;

        var top = rail.scrollTop + delta;
        if (reduced || !rail.scrollTo) rail.scrollTop = top;
        else rail.scrollTo({ top: top, behavior: 'smooth' });
      }

      rail.addEventListener('scroll', sync, { passive: true });
      if (desktop.addEventListener) desktop.addEventListener('change', sync);

      /* Dawn moves aria-current itself, from clicks, slide changes and variant
         swaps alike — watching the attribute covers every one of those. */
      if ('MutationObserver' in window) {
        new MutationObserver(function () {
          sync();
          reveal();
        }).observe(list, { subtree: true, attributes: true, attributeFilter: ['aria-current'] });
      }

      /* The stage sets the rail's height, so the fades have to be recomputed
         whenever it resizes (breakpoint, zoom, a taller variant image). */
      if ('ResizeObserver' in window) new ResizeObserver(sync).observe(rail);
      else window.addEventListener('resize', sync);

      sync();
      reveal();
    });
  }

  /* ---- Range bars: animate on reveal --------------------------------- */
  function initRangeBars(root) {
    if (reduced || !('IntersectionObserver' in window)) {
      each(root, '.pmb-range__bar', function (bar) { bar.classList.add('is-drawn'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add('is-drawn'); io.unobserve(en.target); }
      });
    }, { threshold: 0.4 });
    each(root, '.pmb-range__bar:not(.is-drawn)', function (bar) { io.observe(bar); });
  }

  function initAll(root) {
    initGalleryRail(root);
    initSpecSheet(root);
    initRangeCalc(root);
    initRangeBars(root);
    initFitMatcher(root);
    initFitment(root);
    initBundle(root);
    initReviewGallery(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
